import { Deployments, Homeless, MemberSearchResult, ReliveOptions, ReliveOptionsDeployment } from 'types';
import { History } from 'history';
import { Dispatch } from 'redux';
import { setIntakeDeploymentId } from 'redux/actions/intake';
import { GENERIC_SIGN_UP_UNAVAILABLE } from 'util/user';

export enum IntakeMemberSearchResultType {
  active = 'active',
  activeRestrictedView = 'activeRestrictedView',
  targetOrEligible = 'targetOrEligible',
  reinstate = 'reinstate',
  reinstateDirect = 'reinstateDirect',
  reenroll = 'reenroll',
  closed = 'closed',
  notEligible = 'notEligible',
  notFound = 'notFound',
}

export type ExtensionRequestKind =
  | 'reinstatement'
  | 'reenrollment'
  | 'activeMembership';

export interface IntakeMemberSearchResult {
  type: IntakeMemberSearchResultType;
  memberResult: MemberSearchResult | null;
  unavailableReason?: string | null;
  extensionRequestKind?: ExtensionRequestKind;
}

class MemberProfileStage {
  static isAlreadyEnrolledMember(profileStage: string): boolean {
    const enrolledStages = ['LIVE', 'NEEDS_REVIEW', 'PENDING'];
    return enrolledStages.includes(profileStage);
  }

  static isEligible(profileStage: string): boolean {
    const eligibleStages = ['TARGET_LIST', 'ELIGIBILITY_LIST'];
    return eligibleStages.includes(profileStage);
  }

  static isInactive(profileStage: string): boolean {
    const inactiveStages = ['EXITED', 'UNSUCCESSFUL_PENDING'];
    return inactiveStages.includes(profileStage);
  }
}

type DedupeableMembership = {
  id: number;
  member_id: number;
  profile_stage: string;
};

/** Prefer LIVE/NEEDS_REVIEW/PENDING; otherwise keep highest membership id. */
export const dedupeMembersByPreferredMembership = <
  T extends DedupeableMembership
>(
  members: T[]
): T[] => {
  return Object.values(
    members.reduce<Record<number, T>>((acc, member) => {
      const key = member.member_id;
      const current = acc[key];

      if (!current) {
        acc[key] = member;
        return acc;
      }

      const memberPreferred = MemberProfileStage.isAlreadyEnrolledMember(
        member.profile_stage
      );
      const currentPreferred = MemberProfileStage.isAlreadyEnrolledMember(
        current.profile_stage
      );

      if (memberPreferred !== currentPreferred) {
        if (memberPreferred) acc[key] = member;
      } else if (member.id > current.id) {
        acc[key] = member;
      }

      return acc;
    }, {})
  );
};

export class IntakeMemberSearchResultHelper {
  static getIntakeMemberSearchResultType(
    searchResult: MemberSearchResult[],
    selectedDeployment: Deployments,
  ): IntakeMemberSearchResult {
    // Deployment choice for inactive members is applied with relive options.
    void selectedDeployment;
    //if search result is empty, return not found
    if (!searchResult || searchResult.length === 0) {
      return { type: IntakeMemberSearchResultType.notFound, memberResult: null };
    }

    // Check active membership (should only be one so first/last doesn't matter)
    const activeMembership = searchResult.find((result) =>
      MemberProfileStage.isAlreadyEnrolledMember(result.profile_stage)
    );

    if (activeMembership) {
      if (activeMembership.can_user_view_member === false) {
        return {
          type: IntakeMemberSearchResultType.activeRestrictedView,
          memberResult: activeMembership,
        };
      } else {
        return {
          type: IntakeMemberSearchResultType.active,
          memberResult: activeMembership,
        };
      }
    }

    // Searching for target list
    const targetListResult = searchResult.filter((result) =>
      MemberProfileStage.isEligible(result.profile_stage)
    );
    
    if (targetListResult.length > 0 && targetListResult.length === 1) {
      const eligibleMembership = targetListResult[0];
      return {
        type: IntakeMemberSearchResultType.targetOrEligible,
        memberResult: eligibleMembership,
      };
    }

    // Inactive members are classified with relive options, not phase comparison.
    const latestMembership = searchResult[0];
    if (latestMembership && MemberProfileStage.isInactive(latestMembership.profile_stage)) {
      return {
        type: IntakeMemberSearchResultType.reenroll,
        memberResult: latestMembership,
      };
    }

    return {
      type: IntakeMemberSearchResultType.notEligible,
      memberResult: null,
    };
  }
}

export const RELIVE_OPTIONS_UNAVAILABLE_REASON =
  "We couldn't confirm whether this membership can be extended. Please try again.";

// "max duration used up" is the re-enrollment short reason.
// Reinstate returns the friendly copy, which says "maximum time" (or "max time").
export const isMaxDurationReasoning = (reason?: string | null): boolean => {
  const text = (reason ?? '').toLowerCase();
  return (
    text.includes('max duration') ||
    text.includes('maximum time') ||
    text.includes('max time')
  );
};

export type InactiveMembershipAction =
  | 'reinstateDirect'
  | 'extensionForm'
  | 'reenroll'
  | 'closed';

const EXTENSION_REQUEST_LABEL: Record<ExtensionRequestKind, string> = {
  reinstatement: 'Reinstatement Extension Request',
  reenrollment: 'Reenrollment Extension Request',
  activeMembership: 'Active Membership Extension Request',
};

export const extensionRequestDescription = (
  kind: ExtensionRequestKind,
  details: string
): string => `${EXTENSION_REQUEST_LABEL[kind]}: ${details}`;

// Same membership (eligible to reinstate, or blocked because this membership
// already used its maximum time) is a reinstatement. A new membership blocked
// by max duration, including a different deployment, is a reenrollment.
export const inactiveExtensionRequestKind = (
  reliveOptions: ReliveOptions,
  sameDeployment: boolean
): Extract<ExtensionRequestKind, 'reinstatement' | 'reenrollment'> => {
  if (
    sameDeployment &&
    (reliveOptions.can_reinstate ||
      isMaxDurationReasoning(reliveOptions.cannot_reinstate_reasoning))
  ) {
    return 'reinstatement';
  }

  return 'reenrollment';
};

// Same deployment: direct reinstate posts with no body. A reinstate that is
// eligible but not direct, or a max-duration block, opens the extension form.
// A different deployment only opens that form when its own reason is max duration.
export const inactiveMembershipAction = (
  reliveOptions: ReliveOptions,
  {
    sameDeployment,
    shortReasoning,
    canSignUp,
  }: {
    sameDeployment: boolean;
    shortReasoning?: string | null;
    canSignUp?: boolean;
  }
): InactiveMembershipAction => {
  const maxDuration =
    isMaxDurationReasoning(shortReasoning) ||
    (sameDeployment &&
      isMaxDurationReasoning(reliveOptions.cannot_reinstate_reasoning));

  if (sameDeployment && reliveOptions.can_reinstate_directly) {
    return 'reinstateDirect';
  }

  if ((sameDeployment && reliveOptions.can_reinstate) || maxDuration) {
    return 'extensionForm';
  }

  if (canSignUp) {
    return 'reenroll';
  }

  return 'closed';
};

export const isMembershipEndingWithinOneMonth = (
  endDate?: string | null,
  now: Date = new Date()
): boolean => {
  if (!endDate) return false;

  const parsed = new Date(endDate);
  if (Number.isNaN(parsed.getTime())) return false;

  const endDay = new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate()
  );
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const limit = new Date(today);
  limit.setMonth(limit.getMonth() + 1);

  return endDay.getTime() >= today.getTime() && endDay.getTime() <= limit.getTime();
};

// Whole days from today through the membership end date. Today is 0.
// Past or unreadable dates return null.
export const daysUntilMembershipEnd = (
  endDate?: string | null,
  now: Date = new Date()
): number | null => {
  if (!endDate) return null;

  const parsed = new Date(endDate);
  if (Number.isNaN(parsed.getTime())) return null;

  const endDay = new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate()
  );
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;

  return Math.round((endDay.getTime() - today.getTime()) / msPerDay);
};

export const membershipExpiringLabel = (days: number): string => {
  if (days === 0) return 'MEMBERSHIP EXPIRING IN: TODAY';
  if (days === 1) return 'MEMBERSHIP EXPIRING IN: TOMORROW';
  return `MEMBERSHIP EXPIRING IN: ${days} DAYS`;
};

// Banner copy only. A direct reinstate says "Reactivate Membership".
// A request (eligible but not direct, or max duration) says "Request Extension"
// even when another deployment is also available.
// An open same-deployment re-enroll, or only a different deployment, says
// "Extend Membership".
export type DeploymentSelectorCopy = {
  title: string;
  subtext: string;
  yesText: string;
  noText: string;
};

type DeploymentChoiceSignals = {
  canSignUp?: boolean;
  shortReasoning?: string | null;
};

// Same deployment can reactivate the current membership or start a new one.
// A different deployment is a new membership. An approved extension uses the
// same sentences as days remaining; the profile strip says it was approved.
const sameDeploymentChoice = (
  reliveOptions: ReliveOptions,
  currentDeployment: DeploymentChoiceSignals | null,
  membershipExtension: boolean
):
  | 'reinstateDaysRemaining'
  | 'reinstateExtensionRequest'
  | 'reinstateExtensionApproved'
  | 'reenrollDaysRemaining'
  | 'reenrollExtensionRequest'
  | 'reenrollExtensionApproved'
  | 'continue' => {
  const action = inactiveMembershipAction(reliveOptions, {
    sameDeployment: true,
    shortReasoning: currentDeployment?.shortReasoning,
    canSignUp: currentDeployment?.canSignUp,
  });

  if (action === 'reinstateDirect') {
    return membershipExtension
      ? 'reinstateExtensionApproved'
      : 'reinstateDaysRemaining';
  }

  if (action === 'reenroll') {
    return membershipExtension
      ? 'reenrollExtensionApproved'
      : 'reenrollDaysRemaining';
  }

  if (action === 'extensionForm') {
    return inactiveExtensionRequestKind(reliveOptions, true) === 'reinstatement'
      ? 'reinstateExtensionRequest'
      : 'reenrollExtensionRequest';
  }

  return 'continue';
};

const differentDeploymentChoice = (
  otherDeployments: DeploymentChoiceSignals[],
  membershipExtension: boolean
):
  | 'reenrollDaysRemaining'
  | 'reenrollExtensionRequest'
  | 'reenrollExtensionApproved'
  | 'mixed' => {
  const choices = otherDeployments.map((deployment) => {
    if (deployment.canSignUp) {
      return membershipExtension
        ? 'reenrollExtensionApproved'
        : 'reenrollDaysRemaining';
    }
    if (isMaxDurationReasoning(deployment.shortReasoning)) {
      return 'reenrollExtensionRequest';
    }
    return null;
  }).filter((choice): choice is 'reenrollDaysRemaining' | 'reenrollExtensionRequest' | 'reenrollExtensionApproved' => choice != null);

  const unique = Array.from(new Set(choices));
  if (unique.length === 1) return unique[0];
  return 'mixed';
};

export const deploymentSelectorCopy = ({
  memberName,
  deploymentName,
  reliveOptions,
  currentDeployment,
  otherDeployments,
  membershipExtension,
}: {
  memberName: string;
  deploymentName?: string | null;
  reliveOptions: ReliveOptions;
  currentDeployment: DeploymentChoiceSignals | null;
  otherDeployments: DeploymentChoiceSignals[];
  membershipExtension: boolean;
}): DeploymentSelectorCopy => {
  const name = memberName.trim() || 'this member';
  const deployment = deploymentName?.trim() || 'this deployment';
  const same = sameDeploymentChoice(
    reliveOptions,
    currentDeployment,
    membershipExtension
  );
  const different = differentDeploymentChoice(
    otherDeployments,
    membershipExtension
  );

  const sameCopy = {
    reinstateDaysRemaining: {
      title: `Reactivate ${name}'s Membership`,
      subtext: `Will ${name}'s Samaritan Membership still be through ${deployment}?`,
      yesText: `Yes, reactivate their Membership through ${deployment}`,
    },
    reinstateExtensionRequest: {
      title: `Request an Extension for ${name}'s Membership`,
      subtext: `Will ${name}'s Samaritan Membership still be through ${deployment}?`,
      yesText: `Yes, submit an extension request through ${deployment}`,
    },
    reinstateExtensionApproved: {
      title: `Reactivate ${name}'s Membership`,
      subtext: `Will ${name}'s Samaritan Membership still be through ${deployment}?`,
      yesText: `Yes, reactivate their Membership through ${deployment}`,
    },
    reenrollDaysRemaining: {
      title: `${name}'s New Membership`,
      subtext: `Will ${name}'s new Samaritan Membership still be through ${deployment}?`,
      yesText: `Yes, sign them up through ${deployment}`,
    },
    reenrollExtensionRequest: {
      title: `Request an Extension for ${name}'s New Membership`,
      subtext: `Will ${name}'s new Samaritan Membership still be through ${deployment}?`,
      yesText: `Yes, submit an extension request for a new Membership through ${deployment}`,
    },
    reenrollExtensionApproved: {
      title: `${name}'s New Membership`,
      subtext: `Will ${name}'s new Samaritan Membership still be through ${deployment}?`,
      yesText: `Yes, sign them up through ${deployment}`,
    },
    continue: {
      title: `Continue ${name}'s Membership`,
      subtext: `Will ${name}'s Samaritan Membership still be through ${deployment}?`,
      yesText: `Yes, continue through ${deployment}`,
    },
  }[same];

  const noText = {
    reenrollDaysRemaining:
      'No, sign them up through a different insurance/deployment',
    reenrollExtensionRequest:
      'No, submit an extension request through a different insurance/deployment',
    reenrollExtensionApproved:
      'No, sign them up through a different insurance/deployment',
    mixed: 'No, continue through a different insurance/deployment',
  }[different];

  return { ...sameCopy, noText };
};

export const profileReliveButtonLabel = ({
  canReinstate,
  canReinstateDirectly,
  currentShortReasoning,
}: {
  canReinstate: boolean;
  canReinstateDirectly?: boolean;
  currentShortReasoning?: string | null;
}): 'Request Extension' | 'Extend Membership' | 'Reactivate Membership' => {
  if (canReinstateDirectly) {
    return 'Reactivate Membership';
  }

  if (canReinstate || isMaxDurationReasoning(currentShortReasoning)) {
    return 'Request Extension';
  }

  return 'Extend Membership';
};

export const reliveUnavailableReason = (reliveOptions: ReliveOptions): string => {
  const cannotReinstateReason = reliveOptions.cannot_reinstate_reasoning?.trim();
  if (cannotReinstateReason) return cannotReinstateReason;

  const longReasons = Array.from(
    new Set(
      reliveOptions.deployments_for_reenrollment
        .map((deployment) => deployment.long_reasoning?.trim())
        .filter((reason): reason is string => Boolean(reason))
    )
  );

  if (longReasons.length === 1) return longReasons[0];
  return GENERIC_SIGN_UP_UNAVAILABLE.longReason;
};

// Closed membership modal. Same phase as the selected reenrollment deployment
// is a reinstate block; a different phase is a sign-up block.
export const closedMembershipUnavailableReason = (
  member: MemberSearchResult,
  selectedDeploymentId: number,
  reliveOptions: ReliveOptions
): string => {
  const selectedDeployment = reliveOptions.deployments_for_reenrollment.find(
    (deployment) => deployment.deployment_id === selectedDeploymentId
  );

  const samePhase =
    selectedDeployment != null &&
    selectedDeployment.phase_id != null &&
    member.phase_id != null &&
    selectedDeployment.phase_id === member.phase_id;

  if (samePhase) {
    return (
      reliveOptions.cannot_reinstate_reasoning?.trim() ||
      GENERIC_SIGN_UP_UNAVAILABLE.longReason
    );
  }

  return (
    selectedDeployment?.long_reasoning?.trim() ||
    GENERIC_SIGN_UP_UNAVAILABLE.longReason
  );
};

export const resolveInactiveMembershipResult = (
  member: MemberSearchResult,
  selectedDeploymentId: number,
  reliveOptions: ReliveOptions
): IntakeMemberSearchResult => {
  const reenrollment = reliveOptions.deployments_for_reenrollment.find(
    (deployment) => deployment.deployment_id === selectedDeploymentId
  );

  const action = inactiveMembershipAction(reliveOptions, {
    sameDeployment: member.deployment_id === selectedDeploymentId,
    shortReasoning: reenrollment?.short_reasoning,
    canSignUp: reenrollment?.can_sign_up,
  });

  if (action === 'reinstateDirect') {
    return {
      type: IntakeMemberSearchResultType.reinstateDirect,
      memberResult: member,
      unavailableReason: null,
    };
  }

  if (action === 'extensionForm') {
    return {
      type: IntakeMemberSearchResultType.reinstate,
      memberResult: member,
      unavailableReason: null,
      extensionRequestKind: inactiveExtensionRequestKind(
        reliveOptions,
        member.deployment_id === selectedDeploymentId
      ),
    };
  }

  if (action === 'reenroll') {
    return {
      type: IntakeMemberSearchResultType.reenroll,
      memberResult: member,
      unavailableReason: null,
    };
  }

  const unavailableReason = closedMembershipUnavailableReason(
    member,
    selectedDeploymentId,
    reliveOptions
  );

  return {
    type: IntakeMemberSearchResultType.closed,
    memberResult: member,
    unavailableReason,
  };
};

export const deploymentFromReliveOption = (
  option: ReliveOptionsDeployment,
  knownDeployments: Deployments[]
): Deployments => {
  const known = knownDeployments.find(
    (deployment) => deployment.id === option.deployment_id
  );

  if (!known) {
    return {
      id: option.deployment_id,
      name: option.deployment_name,
      is_eligibility_check_required: false,
      is_grandfathered: false,
      monthly_bonus_pool_max: 0,
      organization_ids: [],
      can_sign_up: option.can_sign_up,
      short_reasoning: option.short_reasoning,
      long_reasoning: option.long_reasoning,
    };
  }

  return {
    ...known,
    name: option.deployment_name || known.name,
    can_sign_up: option.can_sign_up,
    short_reasoning: option.short_reasoning,
    long_reasoning: option.long_reasoning,
  };
};

export const handleStartRegistration = ({
    member,
    confirmedDeployment,
    history,
    autofillIntake,
    dispatch,
}: {
    member: MemberSearchResult;
    confirmedDeployment: Deployments;
    history: History;
    autofillIntake: (member: MemberSearchResult) => void;
    dispatch: Dispatch;
}) => {
    dispatch(setIntakeDeploymentId(confirmedDeployment.id));
    autofillIntake(member);
    history.push(`/intake`);
};

export const convertToMemberSearchResult = (member: Homeless): MemberSearchResult => {
  return {
    id: member.id,
    name: member.name,
    email: member.email,
    phone: member.phone,
    date_of_birth: member.birthday,
    profile_stage: member.profile_stage,
    beacon_id: member.beacon_id,
    language_preference: member.language_preference,
    photo: member.photo,
    mobile_photo: member.mobile_photo,
    notification_photo: member.notification_photo,
    pii_member_id: member.member_id,
    pii_first_name: member.first_name,
    pii_last_name: member.last_name,
  };
};